import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Inbox, Pencil, Plus } from 'lucide-react';
import { useCustomer } from '@/api/customers';
import { KollabrAttributes, VerifiedBadge } from './KollabrProfile';
import { useTicketsInfinite } from '@/api/tickets';
import { Avatar, Badge, Button, Card, EmptyState, ErrorState, Spinner, ListSkeleton } from '@/components/ui';
import { useAuth } from '@/app/auth/useAuth';
import { formatDate, formatRelative, humanize } from '@/lib/format';
import { NewTicketModal } from '../tickets/NewTicketModal';
import { ChannelIcon, PriorityBadge, StatusBadge } from '../tickets/TicketBadges';
import { CustomerFormModal } from './CustomerFormModal';

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-slate-800">{children}</dd>
    </div>
  );
}

export function CustomerPage() {
  const { customerId } = useParams();
  const { can } = useAuth();
  const customer = useCustomer(customerId);
  const tickets = useTicketsInfinite({ customerId });
  const [editing, setEditing] = useState(false);
  const [newTicket, setNewTicket] = useState(false);
  const rows = tickets.data?.pages.flatMap((p) => p.data) ?? [];

  if (customer.isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner size="lg" label="Loading customer" />
      </div>
    );
  }
  if (customer.error) {
    return (
      <Card>
        <ErrorState error={customer.error} onRetry={() => void customer.refetch()} />
      </Card>
    );
  }
  const c = customer.data;

  return (
    <div className="mx-auto max-w-6xl">
      <Link
        to="/customers"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Customers
      </Link>
      <div className="flex flex-col gap-5 lg:flex-row">
        <Card className="relative overflow-hidden p-5 lg:w-80 lg:shrink-0 lg:self-start">
          <div className="brand-gradient absolute inset-x-0 top-0 h-14 opacity-90" aria-hidden />
          <div className="relative pt-10">
            <div className="flex items-start gap-3">
              <Avatar name={c.name} src={c.avatarUrl} size="lg" className="rounded-full ring-4 ring-white" />
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-bold">{c.name}</h1>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  <Badge tone="brand">{humanize(c.type)}</Badge>
                  {c.plan && <Badge>{humanize(c.plan)}</Badge>}
                </div>
              </div>
              {can('update', 'Customer') && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Edit customer"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
            <dl className="mt-5 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Field label="Email">
                  {c.email ? (
                    <a href={`mailto:${c.email}`} className="text-brand-700 hover:underline">
                      {c.email}
                    </a>
                  ) : (
                    '—'
                  )}
                </Field>
              </div>
              <Field label="Phone">{c.phone ?? '—'}</Field>
              <Field label="City">{c.city ?? '—'}</Field>
              <Field label="Platform user ID">
                {c.kollabrUserId ? <span className="font-mono text-xs">{c.kollabrUserId}</span> : '—'}
              </Field>
              <Field label="Customer since">{formatDate(c.createdAt)}</Field>
              <Field label="Tickets">
                {c.ticketCount} total · {c.openTicketCount} open
              </Field>
              <Field label="Last seen">{formatRelative(c.lastSeenAt)}</Field>
            </dl>
            <div className="mt-4 space-y-3">
              <VerifiedBadge at={c.identityVerifiedAt} />
              <KollabrAttributes customer={c} />
            </div>
            {c.tags.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {c.tags.map((t) => (
                  <Badge key={t} tone={t === 'vip' ? 'accent' : 'neutral'}>
                    {t}
                  </Badge>
                ))}
              </div>
            )}
            <div className="mt-5 border-t border-slate-100 pt-4">
              <p className="text-xs font-medium text-slate-500">Notes</p>
              <p className="mt-1 text-sm whitespace-pre-line text-slate-700">
                {c.notes || <span className="text-slate-400">No notes yet.</span>}
              </p>
            </div>
          </div>
        </Card>

        <Card className="min-w-0 flex-1 overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h2 className="text-base font-bold">Conversation history</h2>
            {can('create', 'Ticket') && (
              <Button size="sm" variant="secondary" onClick={() => setNewTicket(true)}>
                <Plus className="h-4 w-4" /> New ticket
              </Button>
            )}
          </div>
          {tickets.isPending ? (
            <ListSkeleton rows={4} label="Loading tickets" />
          ) : tickets.error ? (
            <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState icon={<Inbox className="h-6 w-6" />} title="No tickets yet" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {rows.map((t) => (
                <li key={t.id}>
                  <Link to={`/inbox/${t.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50">
                    <ChannelIcon channel={t.channel} className="mt-1" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">
                        <span className="text-slate-400">#{t.number}</span> {t.subject}
                      </p>
                      <p className="truncate text-sm text-slate-500">{t.lastMessagePreview}</p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className="flex gap-1.5">
                        <PriorityBadge priority={t.priority} />
                        <StatusBadge status={t.status} />
                      </span>
                      <span className="text-xs text-slate-400">{formatRelative(t.lastMessageAt)}</span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {tickets.hasNextPage && (
            <div className="flex justify-center border-t border-slate-100 p-3">
              <Button
                variant="ghost"
                size="sm"
                loading={tickets.isFetchingNextPage}
                onClick={() => void tickets.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          )}
        </Card>
      </div>

      {editing && <CustomerFormModal customer={c} onClose={() => setEditing(false)} />}
      {newTicket && <NewTicketModal customer={c} onClose={() => setNewTicket(false)} />}
    </div>
  );
}
